import React, { useMemo } from 'react'
import { TabContext } from '@mui/lab'
import { Box, List } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  isCemFeatureEnabled,
  isFeatureFlagEnabled,
} from '../../../rsmCoreComponents/utils/featureFlagUtils'
import {
  getUserInfo,
  getCemFeatures,
} from '../../../store/userInfo/userInfoSlice'
import { Styles } from '../../../types'
import {
  CEM_FEATURE_PAYMENT_PAY_INVOICES,
  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
  CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
  CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
} from '../../../utils/constants/constants'
import RouterNavListItem from '../../Navigation/NavListItem/RouterNavListItem'
import DropdownNavListItem from '../../Navigation/NavListItem/DropDownNavListItem'
import tokens from '../../../styles/tokens.json'

const styles: Styles = {
  nav: (theme) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    height: '100%',
    [theme.breakpoints.down('desktop')]: {
      display: 'none',
    },
  }),
  list: (theme) => ({
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    border: 'none',
    height: '100%',
    gap: '2.5rem',
    a: {
      '&[aria-current="true"]': {
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 'calc(100% - 0.125rem)',
          right: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '0.125rem',
          backgroundColor: theme.palette.secondary.main,
          zIndex: 1,
        },
      },
    },
  }),
  link: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1rem',
    textDecoration: 'none',
    color: tokens.colors.white,
    whiteSpace: 'nowrap',
    ':hover': {
      fontFamily: tokens.type.fontFamilies.preloBold,
    },
    '&[aria-current="true"]': {
      fontFamily: tokens.type.fontFamilies.preloBold,
    },
    '&[aria-current="true"]::after': {
      backgroundColor: `${tokens.colors.rsmBlue.primary} !important`, // important is needed to override theme styling
    },
    ':focus-visible': {
      outline: `.125rem solid ${tokens.colors.white}`,
      boxShadow: ` 0 0 0 .125rem ${tokens.colors.black}`,
    },
  },
}

type DesktopMenuProps = {
  currentItem?: string
  showWelcome?: boolean
}

const DesktopMenu = ({
  currentItem = '',
  showWelcome = false,
}: DesktopMenuProps) => {
  const { t } = useTranslation()
  const cemFeatures = useSelector(getCemFeatures)
  const userInfo = useSelector(getUserInfo)
  const isUIRedesignEnabled = isFeatureFlagEnabled(
    'UI_Redesign',
    userInfo.emailAddress,
    cemFeatures,
  )

  const invoicingEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [
          CEM_FEATURE_PAYMENT_PAY_INVOICES,
          CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
          CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
        ],
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )
  const questionnairesEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [
          CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
          CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
        ],
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )

  const engagementsEnabled = isFeatureFlagEnabled(
    'Targeting_Engagement',
    userInfo.emailAddress,
    cemFeatures,
  )

  return (
    <Box component="nav" sx={styles.nav} aria-label={t('Navigation.Main')}>
      <TabContext value={currentItem}>
        <List sx={styles.list}>
          <RouterNavListItem
            to="/"
            value="home"
            testId="Lnk_Navigation_Home"
            linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
            {t('Navigation.Home')}
          </RouterNavListItem>

          {engagementsEnabled && (
            <RouterNavListItem
              to="/engagements"
              value="engagements"
              testId="Lnk_Navigation_Engagements"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.Engagements')}
            </RouterNavListItem>
          )}

          <RouterNavListItem
            to="/insights"
            value="insights"
            testId="Lnk_Navigation_Insights"
            linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
            {t('Navigation.Insights')}
          </RouterNavListItem>
          {isFeatureFlagEnabled(
            'BI_Reporting',
            userInfo.emailAddress,
            cemFeatures,
          ) && (
            <RouterNavListItem
              to="/report/demo-3"
              value="report"
              testId="Lnk_Navigation_Report"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.Reports')}
            </RouterNavListItem>
          )}
          {isFeatureFlagEnabled(
            'Invoicing',
            userInfo.emailAddress,
            cemFeatures,
          ) &&
            invoicingEnabled && (
              <RouterNavListItem
                to="/invoicing"
                value="invoicing"
                testId="Lnk_Navigation_Invoicing"
                linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
                {t('Navigation.PayMyBill')}
              </RouterNavListItem>
            )}
          {isFeatureFlagEnabled(
            'Questionnaires',
            userInfo.emailAddress,
            cemFeatures,
          ) &&
            questionnairesEnabled && (
              <RouterNavListItem
                to="/questionnaires"
                value="questionnaires"
                testId="Lnk_Navigation_Questionnaires"
                linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
                {t('Navigation.Questionnaire.MyQuestionnaires')}
              </RouterNavListItem>
            )}
          {!isFeatureFlagEnabled(
            'Support',
            userInfo.emailAddress,
            cemFeatures,
          ) && (
            <RouterNavListItem
              to="/support"
              value="support"
              testId="Lnk_Navigation_Support"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.Support')}
            </RouterNavListItem>
          )}
          {isFeatureFlagEnabled(
            'Support',
            userInfo.emailAddress,
            cemFeatures,
          ) &&
          isFeatureFlagEnabled(
            'UI_Redesign',
            userInfo.emailAddress,
            cemFeatures,
          ) ? (
            <DropdownNavListItem
              label={t('Navigation.ContactUs')}
              testId="Lnk_Navigation_contact-us"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}
              items={[
                {
                  label: t('Navigation.Support'),
                  to: '/contact-us/support-page',
                  testId: 'Lnk_Navigation_SupportDropdown',
                },
                {
                  label: t('Navigation.Feedback'),
                  to: '/contact-us/feedback',
                  testId: 'Lnk_Navigation_FeedbackDropdown',
                },
                {
                  label: t('Navigation.FAQ'),
                  to: '/contact-us/faq',
                  testId: 'Lnk_Navigation_FAQDropdown',
                },
              ]}
              menuStyle={
                showWelcome
                  ? undefined
                  : {
                      backgroundColor: tokens.colors.black,
                      border: `0.0625rem solid ${tokens.colors.black}`,
                      '& a': {
                        color: tokens.colors.white,
                        '&:hover': {
                          backgroundColor: tokens.colors.white,
                          color: tokens.colors.black,
                        },
                      },
                    }
              }
            />
          ) : (
            <RouterNavListItem
              to="/contact-us"
              value="contact-us"
              testId="Lnk_Navigation_contact-us"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.ContactUs')}
            </RouterNavListItem>
          )}
        </List>
      </TabContext>
    </Box>
  )
}

export default DesktopMenu
